import { useEffect, useRef } from "react";
const FOCUSABLE = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
].join(", ");
/**
 * Traps keyboard focus inside the referenced element while `active` is true.
 * Also closes on Escape.
 *
 * Usage:
 *   const ref = useFocusTrap(isOpen, onClose);
 *   <div ref={ref}>…modal content…</div>
 */
export function useFocusTrap(active, onClose) {
    const ref = useRef(null);
    // Remember what had focus before the modal opened so we can restore it
    const previousFocus = useRef(null);
    useEffect(() => {
        if (!active)
            return;
        previousFocus.current = document.activeElement;
        // Focus the first focusable element inside the trap
        const el = ref.current;
        if (el) {
            const first = el.querySelectorAll(FOCUSABLE)[0];
            first?.focus();
        }
        return () => {
            // Restore focus when modal closes
            if (previousFocus.current instanceof HTMLElement) {
                previousFocus.current.focus();
            }
        };
    }, [active]);
    useEffect(() => {
        if (!active)
            return;
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose();
                return;
            }
            if (e.key !== "Tab")
                return;
            const el = ref.current;
            if (!el)
                return;
            const focusable = Array.from(el.querySelectorAll(FOCUSABLE));
            if (focusable.length === 0)
                return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey) {
                // Shift+Tab: if focus is on first element, wrap to last
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            }
            else {
                // Tab: if focus is on last element, wrap to first
                if (document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [active, onClose]);
    return ref;
}
